/**
 * Quais colunas da mesa existem nesta largura — e é a ÚNICA resposta.
 *
 * A conta morava dentro de `Casca.jsx` e era refeita em `Conversa.jsx` (a
 * largura que sobra para a conversa) e em `CabecalhoDaConversa.jsx` (que
 * perguntava `useCelular()` no lugar dela). Três leitores, duas contas
 * diferentes, e o defeito exatamente onde elas discordavam: entre 880 e 1179px
 * a lista sai da tela para a ficha caber, mas a janela não é "celular" — então
 * o ← de voltar não era montado e a conversa não tinha saída, num notebook de
 * 1366 com a janela pela metade ou num iPad na horizontal.
 *
 * Aqui é função pura de propósito: é uma decisão, e decisão desta casa se prende
 * em teste (`casca.test.jsx`), não em print.
 */

/** Abaixo daqui é telefone: uma coluna por vez, faixa em cima, abas embaixo. */
export const CELULAR = 760;

/** A partir daqui a ficha do caso EMPURRA em vez de flutuar por cima. */
export const PAINEL_EMPURRA = 880;

/** A partir daqui cabem as três colunas: lista, conversa e ficha. */
export const TRES_COLUNAS = 1180;

/**
 * O TRILHO come largura quando está fixado, e a conta tem de saber disso.
 *
 * A largura que chegava aqui era a da JANELA, e o trilho fixado ocupa 246px
 * dela. Numa janela de 1200 com o trilho aberto sobram 954px para as colunas — a
 * conta achava que havia 1200, mantinha as três, e a conversa ficava com 244px:
 * bolha de duas palavras por linha. Aparece sozinho em desktop com ZOOM alto,
 * que é onde a largura em pixels de CSS encolhe sem a janela mudar de tamanho
 * (1920 a 200% são 960), e ninguém suspeita da tela — ela só fica ruim.
 *
 * Fica aqui, e não em `Casca.jsx`, pelo mesmo motivo do resto: é UMA decisão de
 * largura, e decisão desta casa se prende em teste.
 */
export const semOTrilho = (largura, trilhoFixado) => Math.max(0, largura - (trilhoFixado ? 246 : 56));

/**
 * @param {{largura: number, emAtendimentos: boolean, focoNoChat: boolean, trilhoFixado?: boolean, listaRecolhida?: boolean, painelRecolhido?: boolean}} onde
 * @returns {{celular: boolean, empurraPainel: boolean, mostrarLista: boolean, mostrarConteudo: boolean, larguraUtil: number}}
 */
export function colunasDaMesa({
  largura,
  emAtendimentos,
  focoNoChat,
  trilhoFixado = false,
  listaRecolhida = false,
  painelRecolhido = false,
}) {
  const celular = largura < CELULAR;
  /* No celular não há trilho (a navegação é a barra de abas), então a largura é
     a da janela; no computador ele sempre reserva ao menos os 56px fechados. */
  const larguraUtil = celular ? largura : semOTrilho(largura, trilhoFixado);
  /* Recolher é gesto de quem olha: a ficha para de empurrar e a conversa fica
     com a largura toda. Continua sendo a mesma coluna, só retraída — reabre
     pela alça na borda. */
  /*
   * CABE empurrar × ESTÁ empurrando — e confundir as duas quebrou o interruptor.
   *
   * `cabeEmpurrar` é geometria: nesta largura, com uma conversa aberta, a ficha
   * é COLUNA da mesa e não gaveta. `empurraPainel` é o estado de agora, que
   * inclui a escolha de quem olha (`painelRecolhido`).
   *
   * Quem decide COMO alternar a ficha precisa da primeira, nunca da segunda: com
   * a ficha recolhida, `empurraPainel` é falso, e o interruptor concluía "então
   * ela é gaveta" e mexia em `?painel=` — que não é o que governa a coluna em
   * tela larga. `painelRecolhido` ficava preso em `true`, e o botão fechava a
   * ficha uma vez e não a trazia nunca mais de volta.
   */
  const cabeEmpurrar = emAtendimentos && focoNoChat && larguraUtil >= PAINEL_EMPURRA;
  const empurraPainel = cabeEmpurrar && !painelRecolhido;
  /* A lista é sacrificada quando a ficha empurra e não há espaço para as três:
     entre as duas a ficha ganha, porque é ela que tem o próximo passo do caso. */
  const listaEscondida = !celular && empurraPainel && larguraUtil < TRES_COLUNAS;
  return {
    celular,
    empurraPainel,
    cabeEmpurrar,
    larguraUtil,
    /* Sai daqui para a casca porque o BOTÃO de alternar a lista precisa saber
       que ela não cabe: nesta faixa alternar não mudaria nada, e botão que não
       muda nada é a tela mentindo. */
    listaEscondida,
    /* Recolher a lista vale só no computador: no celular ela É a tela, e uma
       alça de 14px no lugar dela deixaria a pessoa sem lista e sem conversa. */
    mostrarLista: emAtendimentos && (celular ? !focoNoChat : !listaEscondida && !(listaRecolhida && focoNoChat)),
    mostrarConteudo: !celular || !emAtendimentos || focoNoChat,
  };
}
